// COGRAD FELLOWSHIP — Redesigned
import React, { useEffect, useRef } from "react";
import "./CogradFellowship.scss";
import Header from "../components/Header";
import Footer from "../components/Footer";
import FellowshipForm from "../components/FellowshipForm";
import { ArrowUpRight } from "iconoir-react";
import {
  FaDollarSign, FaGraduationCap, FaChartLine, FaGlobeAmericas,
  FaPlay, FaStar
} from "react-icons/fa";
import AOS from "aos";
import "aos/dist/aos.css";
import { Helmet } from "react-helmet";


const CogradFellowship = () => {
  const formRef = useRef(null);

  useEffect(() => {
    AOS.init({ duration: 500, once: true, offset: 30, easing: 'ease-out' });
    const t = setTimeout(() => AOS.refreshHard(), 100);
    window.scrollTo(0, 0);
    return () => clearTimeout(t);
  }, []);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const benefits = [
    {
      num: "01",
      icon: <FaDollarSign />,
      title: "Financial Support",
      desc: "Receive a competitive research stipend to support your academic journey and innovative projects without financial worry.",
    },
    {
      num: "02",
      icon: <FaGraduationCap />,
      title: "Expert Mentorship",
      desc: "Connect with industry leaders and academic experts who will guide your research and professional growth at every step.",
    },
    {
      num: "03",
      icon: <FaChartLine />,
      title: "Career Growth",
      desc: "Access specialized training, professional development workshops, and networking opportunities to accelerate your career.",
    },
    {
      num: "04",
      icon: <FaGlobeAmericas />,
      title: "Global Opportunities",
      desc: "Work on international projects and expand your network with researchers and professionals from around the world.",
    },
  ];

  const steps = [
    {
      num: "01",
      title: "Fill Out Application",
      desc: "Complete our comprehensive online form with details about your background and research interests.",
    },
    {
      num: "02",
      title: "Submit CV & Proposal",
      desc: "Share your academic background along with a well-structured research proposal aligned with Cograd's mission.",
    },
    {
      num: "03",
      title: "Fellowship Interview",
      desc: "Selected candidates are invited for an interview with our fellowship committee to discuss their application in depth.",
    },
    {
      num: "04",
      title: "Final Selection",
      desc: "Successful applicants are welcomed into the Cograd Fellowship Program with a formal onboarding process.",
    },
  ];

  const testimonials = [
    {
      text: "The Cograd Fellowship transformed my career trajectory. The mentorship and research opportunities helped me develop cutting-edge skills that set me apart in the industry.",
      author: "Dr. Vikash Yadav",
      role: "AI Researcher, Former Cograd Fellow",
      initials: "VY",
    },
    {
      text: "Being part of the Cograd Fellowship opened doors I never thought possible. The collaborative environment connected me with leaders across the field.",
      author: "Aditya Dubey",
      role: "Data Scientist, Fellowship Alum",
      initials: "AD",
    },
    {
      text: "The structured research program and personalized guidance helped me publish my first academic paper. This fellowship is invaluable for aspiring researchers.",
      author: "Priya Sharma",
      role: "PhD Candidate, Cograd Fellow 2024",
      initials: "PS",
    },
    {
      text: "What sets Cograd apart is the emphasis on both theoretical knowledge and practical application. I gained hands-on experience on real-world projects that boosted my portfolio.",
      author: "Sangeetha Sundaram",
      role: "Machine Learning Engineer, Former Fellow",
      initials: "SS",
    },
  ];

  const marqueeItems = [
    "Join Cograd Fellowship",
    "Apply Now — 2026-27 Cohort",
    "Research Stipend Available",
    "Expert Mentorship",
    "Global Opportunities",
    "Career Advancement",
  ];

  return (
    <div className="fp">
      <Helmet>
        <title>Cograd Fellowship Program — Research Stipend &amp; Mentorship</title>
        <meta
          name="description"
          content="Join the Cograd Fellowship Program for financial support, expert mentorship, career growth opportunities, and international projects."
        />
      </Helmet>

      <Header />

      {/* ── HERO ── */}
      <section className="fp-hero">
        <div className="fp-hero__bg">
          <div className="fp-hero__grid" />
          <div className="fp-hero__glow fp-hero__glow--a" />
          <div className="fp-hero__glow fp-hero__glow--b" />
        </div>

        <div className="fp-hero__inner">
          <div className="fp-hero__badge" data-aos="fade-down">
            <span className="fp-hero__badge-dot" />
            Applications Open — 2026-27 Cohort
          </div>

          <h1 className="fp-hero__h1" data-aos="fade-up">
            Build Your Research
            <br />
            Career with <span className="fp-hero__h1-blue">Cograd</span>
          </h1>

          <p className="fp-hero__sub" data-aos="fade-up" data-aos-delay="80">
            Join a community of innovators shaping the future of education and
            technology. Get mentorship, stipend, and global exposure.
          </p>

          <div className="fp-hero__cta" data-aos="fade-up" data-aos-delay="160">
            <button className="fp-btn fp-btn--primary" onClick={scrollToForm}>
              Apply Now <ArrowUpRight width={18} height={18} />
            </button>
            <a
              href="https://www.youtube.com/watch?v=7WlOKaR_rgM"
              target="_blank"
              rel="noopener noreferrer"
              className="fp-btn fp-btn--ghost"
            >
              <span className="fp-btn__play-icon">
                <FaPlay />
              </span>
              Watch Video
            </a>
          </div>

          <div className="fp-hero__stats" data-aos="fade-up" data-aos-delay="240">
            <div className="fp-hero__stat">
              <span className="fp-hero__stat-n">100+</span>
              <span className="fp-hero__stat-l">Fellows</span>
            </div>
            <div className="fp-hero__stat-div" />
            <div className="fp-hero__stat">
              <span className="fp-hero__stat-n">85%</span>
              <span className="fp-hero__stat-l">Career Advancement</span>
            </div>
            <div className="fp-hero__stat-div" />
            <div className="fp-hero__stat">
              <span className="fp-hero__stat-n">40+</span>
              <span className="fp-hero__stat-l">Research Projects</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── MARQUEE ── */}
      <div className="fp-marquee" aria-hidden="true">
        <div className="fp-marquee__track">
          {[0, 1].map((k) => (
            <div key={k} className="fp-marquee__row">
              {marqueeItems.map((item, i) => (
                <React.Fragment key={i}>
                  <span>{item}</span>
                  <span className="fp-marquee__sep">✦</span>
                </React.Fragment>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* ── BENEFITS ── */}
      <section className="fp-benefits">
        <div className="fp-wrap">
          <div className="fp-section-head" data-aos="fade-up">
            <span className="fp-tag">Why Join</span>
            <h2 className="fp-h2">Everything You Need to Succeed</h2>
            <p className="fp-lead">
              Cograd Fellowship is designed to empower the next generation of
              researchers and innovators with everything they need to thrive.
            </p>
          </div>

          <div className="fp-benefits__grid">
            {benefits.map((b, i) => (
              <div
                key={i}
                className="fp-benefit-card"
                data-aos="fade-up"
                data-aos-delay={i * 80}
              >
                <span className="fp-benefit-card__num">{b.num}</span>
                <div className="fp-benefit-card__icon">{b.icon}</div>
                <h3 className="fp-benefit-card__title">{b.title}</h3>
                <p className="fp-benefit-card__desc">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* ── HOW TO APPLY ── */}
      <section className="fp-process">
        <div className="fp-wrap">
          <div className="fp-section-head" data-aos="fade-up">
            <span className="fp-tag">The Process</span>
            <h2 className="fp-h2">How to Apply</h2>
            <p className="fp-lead">
              A simple four-step process to join the Cograd Fellowship community
              and start your research journey.
            </p>
          </div>

          <div className="fp-process__steps">
            {steps.map((s, i) => (
              <div
                key={i}
                className="fp-process__step"
                data-aos="fade-up"
                data-aos-delay={i * 80}
              >
                <div className="fp-process__step-top">
                  <div className="fp-process__step-num">{s.num}</div>
                  {i < steps.length - 1 && (
                    <div className="fp-process__step-line" />
                  )}
                </div>
                <h3 className="fp-process__step-title">{s.title}</h3>
                <p className="fp-process__step-desc">{s.desc}</p>
              </div>
            ))}
          </div>

          <div className="fp-process__cta" data-aos="fade-up">
            <button
              className="fp-btn fp-btn--primary fp-btn--lg"
              onClick={scrollToForm}
            >
              Apply Now <ArrowUpRight width={20} height={20} />
            </button>
            <p className="fp-process__note">
              Applications for the 2026-27 cohort are now open!
            </p>
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section className="fp-testimonials">
        <div className="fp-wrap">
          <div className="fp-section-head" data-aos="fade-up">
            <span className="fp-tag">Fellow Stories</span>
            <h2 className="fp-h2">What Our Fellows Say</h2>
          </div>

          <div className="fp-testimonials__grid">
            {testimonials.map((t, i) => (
              <div
                key={i}
                className="fp-tcard"
                data-aos="fade-up"
                data-aos-delay={i * 60}
              >
                <span className="fp-tcard__qdeco">&ldquo;</span>
                <div className="fp-tcard__stars">
                  {[...Array(5)].map((_, s) => <FaStar key={s} />)}
                </div>
                <p className="fp-tcard__text">{t.text}</p>
                <div className="fp-tcard__author">
                  <div className="fp-tcard__avatar">{t.initials}</div>
                  <div>
                    <div className="fp-tcard__name">{t.author}</div>
                    <div className="fp-tcard__role">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── APPLICATION FORM ── */}
      <section className="fp-apply" ref={formRef}>
        <div className="fp-apply__bg-grid" />
        <div className="fp-apply__glow" />
        <div className="fp-wrap fp-apply__wrap">
          <div className="fp-section-head" data-aos="fade-up">
            <span className="fp-tag">Apply Today</span>
            <h2 className="fp-h2">Ready to Join the Fellowship?</h2>
            <p className="fp-lead">
              Fill out the application below and take the first step towards
              your research career.
            </p>
          </div>

          <div
            className="fp-apply__form-wrap"
            data-aos="fade-up"
            data-aos-delay="100"
          >
            <FellowshipForm onSubmit={() => {}} />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CogradFellowship;
